import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Megaphone, TrendingUp, TrendingDown, ChevronRight, ArrowLeft, Package, Tag, ShoppingBag, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/emag-campaigns`;

const PLATFORM_LABELS = {
  emag_ro_fbe: "eMAG RO FBE", emag_ro_bau: "eMAG RO BAU",
  emag_bg_fbe: "eMAG BG FBE", emag_bg_bau: "eMAG BG BAU",
  emag_hu_fbe: "eMAG HU FBE", emag_hu_bau: "eMAG HU BAU",
};

const currencyFor = (key) => {
  if (!key) return "RON";
  if (key.includes("_bg_")) return "EUR";
  if (key.includes("_hu_")) return "HUF";
  return "RON";
};

const fmt = (n, c = "RON") => {
  const v = Number(n || 0);
  return `${v.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c}`;
};

function LiftBadge({ value, testId }) {
  const v = Number(value || 0);
  if (v === 0) {
    return <span data-testid={testId} className="text-zinc-500">—</span>;
  }
  const positive = v > 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const cls = positive ? "text-emerald-400" : "text-rose-400";
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 ${cls}`}>
      <Icon size={12} />
      {positive ? "+" : ""}{v.toLocaleString("ro-RO", { maximumFractionDigits: 2 })}
    </span>
  );
}

function CampaignCard({ camp, onOpen }) {
  const c = currencyFor(camp.integration_key);
  const t = camp.sales_totals || {};
  return (
    <div data-testid={`campaign-card-${camp.campaign_id}`}
      onClick={() => onOpen(camp)}
      className="cursor-pointer rounded-xl border border-zinc-800 bg-zinc-950 p-4 transition-all hover:border-orange-500/40 hover:shadow-lg">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="rounded-md bg-orange-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-orange-300">
              {PLATFORM_LABELS[camp.integration_key] || camp.integration_key}
            </span>
            <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-400">
              ID {camp.campaign_id}
            </span>
          </div>
          <h3 className="truncate text-sm font-semibold text-zinc-100">{camp.name}</h3>
          {(camp.start_date || camp.end_date) && (
            <p className="mt-0.5 flex items-center gap-1 text-[11px] text-zinc-500">
              <Calendar size={11} />
              {camp.start_date} → {camp.end_date}
              {camp._using_fallback_window && (
                <span className="ml-1 text-amber-400">(fereastră 14 zile)</span>
              )}
            </p>
          )}
        </div>
        <ChevronRight className="ml-2 text-zinc-500 shrink-0" size={18} />
      </div>

      <div className="mt-3 grid grid-cols-3 gap-3 border-t border-zinc-800 pt-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Produse</div>
          <div className="flex items-baseline gap-1">
            <span className="text-lg font-bold text-zinc-100">{camp.product_count}</span>
            <span className="text-[10px] text-zinc-500">SKU</span>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Reducere medie</div>
          <div className="text-lg font-bold text-amber-300">-{camp.avg_discount_pct}%</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Vânzări</div>
          <div className="text-lg font-bold text-emerald-400">
            {t.qty_net || 0}
            <span className="text-[10px] ml-1 font-normal text-zinc-500">buc</span>
          </div>
        </div>
      </div>

      <div className="mt-3 rounded-lg bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 px-3 py-2">
        <div className="text-[10px] uppercase tracking-wider text-emerald-300">Revenue Net</div>
        <div className="text-xl font-bold text-emerald-300">{fmt(t.revenue_net, c)}</div>
      </div>
    </div>
  );
}

function CampaignDetail({ campaignId, integrationKey, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [applyValue, setApplyValue] = useState(0);
  // Local staging for per-row edits (SKU → {emag_share_ron, cost, discount_pct})
  const [editing, setEditing] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = integrationKey
        ? `${API}/${campaignId}?integration_key=${encodeURIComponent(integrationKey)}`
        : `${API}/${campaignId}`;
      const r = await axios.get(url);
      setData(r.data);
    } catch (e) {
      toast.error("Eroare: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, [campaignId, integrationKey]);

  useEffect(() => { load(); }, [load]);

  const saveType = async (newType) => {
    if (!data) return;
    setSavingConfig(true);
    try {
      await axios.put(`${API}/${campaignId}/config?integration_key=${encodeURIComponent(data.integration_key)}`, {
        type: newType,
        global_value_pct: data.config?.global_value_pct || 0,
      });
      toast.success(`Tip campanie: ${newType === "voucher" ? "Voucher" : "Discount"}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSavingConfig(false); }
  };

  const applyGlobal = async () => {
    if (!data) return;
    setSavingConfig(true);
    try {
      await axios.put(`${API}/${campaignId}/config?integration_key=${encodeURIComponent(data.integration_key)}`, {
        type: data.config?.type || "voucher",
        global_value_pct: Number(applyValue) || 0,
      });
      // Also clear per-row discount overrides so global applies uniformly.
      // (User can still edit each row after this.)
      toast.success(`Aplicat ${applyValue}% pe toate produsele.`);
      setApplyModalOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSavingConfig(false); }
  };

  const saveRow = async (sku) => {
    if (!data) return;
    const draft = editing[sku] || {};
    try {
      await axios.put(
        `${API}/${campaignId}/products/${encodeURIComponent(sku)}?integration_key=${encodeURIComponent(data.integration_key)}`,
        draft,
      );
      toast.success(`Salvat ${sku}`);
      setEditing((prev) => { const n = {...prev}; delete n[sku]; return n; });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  if (loading) return <div className="p-8 text-center text-zinc-500">Se încarcă...</div>;
  if (!data) return null;

  const c = currencyFor(data.integration_key);
  const t = data.totals || {};
  const cfg = data.config || { type: "voucher", global_value_pct: 0 };
  const isVoucher = cfg.type === "voucher";

  return (
    <div className="space-y-4">
      <button onClick={onBack} data-testid="back-to-list"
        className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:border-orange-500/40 hover:text-orange-200">
        <ArrowLeft size={14} /> Înapoi la campanii
      </button>

      {/* Header + Type toggle + Apply global button */}
      <div className="rounded-xl border border-zinc-800 bg-gradient-to-br from-orange-500/10 to-zinc-950 p-5">
        <div className="flex items-center gap-2 mb-1">
          <span className="rounded-md bg-orange-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-orange-300">
            {PLATFORM_LABELS[data.integration_key] || data.integration_key}
          </span>
          <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-400">ID {data.campaign_id}</span>
        </div>
        <h2 className="text-xl font-bold text-zinc-100">{data.name}</h2>
        <p className="mt-1 flex items-center gap-1 text-xs text-zinc-400">
          <Calendar size={12} />
          Perioadă campanie: <span className="font-medium text-zinc-200">{data.date_from} → {data.date_to}</span>
          {data.pre_campaign_from && (
            <span className="ml-2 text-zinc-500">· Pre-campanie: {data.pre_campaign_from} → {data.pre_campaign_to}</span>
          )}
        </p>
        {/* Config controls */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Tip campanie:</span>
          <div className="inline-flex rounded-md border border-zinc-700 overflow-hidden">
            <button
              data-testid="type-voucher-btn"
              onClick={() => saveType("voucher")}
              disabled={savingConfig}
              className={`px-3 py-1.5 text-xs font-medium ${isVoucher ? "bg-orange-500/25 text-orange-100" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"}`}
            >Voucher</button>
            <button
              data-testid="type-discount-btn"
              onClick={() => saveType("discount")}
              disabled={savingConfig}
              className={`px-3 py-1.5 text-xs font-medium ${!isVoucher ? "bg-orange-500/25 text-orange-100" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"}`}
            >Discount</button>
          </div>
          <span className="ml-3 text-[10px] uppercase tracking-wider text-zinc-500">Valoare globală:</span>
          <span className="font-mono text-sm font-semibold text-orange-300">{cfg.global_value_pct || 0}%</span>
          <button
            data-testid="apply-global-btn"
            onClick={() => { setApplyValue(cfg.global_value_pct || 0); setApplyModalOpen(true); }}
            className="ml-2 rounded-md border border-orange-500/40 bg-orange-500/10 px-3 py-1.5 text-xs font-medium text-orange-200 hover:bg-orange-500/20"
          >Setează / Aplică {isVoucher ? "voucher" : "discount"} global</button>
        </div>
      </div>

      {/* Top KPI tabs (campaign vs pre-campaign) */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          testId="kpi-qty"
          label="Bucăți vândute"
          value={`${t.qty_finalized || 0} buc`}
          compare={`vs ${t.pre_qty_finalized || 0} pre-campanie`}
          delta={t.lift_qty}
        />
        <KpiCard
          testId="kpi-real-rev"
          label="Valoare totală vândută (după discount)"
          value={fmt(t.sum_real_total ?? t.revenue_finalized, c)}
          compare={`vs ${fmt(t.pre_revenue_finalized, c)} pre-campanie`}
          delta={t.lift_revenue}
        />
        <KpiCard
          testId="kpi-discount"
          label="Discount oferit (net, din buzunarul nostru)"
          value={fmt(t.sum_our_discount_total || 0, c)}
          compare={`din ${fmt(t.sum_normal_total || 0, c)} valoare la preț normal`}
          delta={null}
          tone="amber"
        />
        <KpiCard
          testId="kpi-profit"
          label="Profit total (după discount)"
          value={fmt(t.profit_total_after_discount || 0, c)}
          extra={t.profit_total_pct != null ? `${t.profit_total_pct}% marjă` : null}
          compare={`vs ${fmt(t.profit_total_normal || 0, c)} (${t.profit_total_normal_pct || 0}%) la preț normal`}
          delta={(t.profit_total_after_discount || 0) - (t.profit_total_normal || 0)}
          tone="emerald"
        />
      </div>

      {/* Products table */}
      <div className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950">
        <div className="flex items-center justify-between border-b border-zinc-800 bg-zinc-900/50 px-4 py-2">
          <div className="text-xs font-semibold text-zinc-100">Produse în campanie ({data.product_count})</div>
          <div className="text-[10px] text-zinc-500">Editează în rând, apoi apasă „Salvează”. Profitul se calculează din costurile din pagina Produse (pret_achizitie + comisioane).</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-900/30 text-[10px] uppercase text-zinc-500">
              <tr>
                <th className="px-3 py-2 text-left">SKU</th>
                <th className="px-3 py-2 text-left">Nume</th>
                <th className="px-3 py-2 text-right">Preț normal</th>
                <th className="px-3 py-2 text-right">{isVoucher ? "Voucher %" : "Discount %"}</th>
                <th className="px-3 py-2 text-right">Preț campanie</th>
                <th className="px-3 py-2 text-right">Discount eMAG (RON)</th>
                <th className="px-3 py-2 text-right">Discount final / buc</th>
                <th className="px-3 py-2 text-right">Profit / buc</th>
                <th className="px-3 py-2 text-right">Buc vândute</th>
                <th className="px-3 py-2 text-right">Sumă totală (la preț normal)</th>
                <th className="px-3 py-2 text-right">Discount total</th>
                <th className="px-3 py-2 text-right">Sumă reală vândută</th>
                <th className="px-3 py-2 text-right">Profit total</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => {
                const draft = editing[p.sku] || {};
                const hasDraft = Object.keys(draft).length > 0;
                return (
                  <tr key={p.sku} className={`border-t border-zinc-800/50 ${hasDraft ? "bg-orange-500/5" : "hover:bg-zinc-900/30"}`} data-testid={`row-${p.sku}`}>
                    <td className="px-3 py-2 font-mono font-medium text-orange-300">{p.sku}</td>
                    <td className="px-3 py-2 max-w-[220px] truncate text-zinc-300" title={p.name}>{p.name || "—"}</td>
                    <td className="px-3 py-2 text-right text-zinc-200 tabular-nums">{fmt(p.normal_price, c)}</td>
                    <td className="px-3 py-2 text-right">
                      <input
                        data-testid={`disc-${p.sku}`}
                        type="number" step="0.1" min="0" max="100"
                        value={draft.discount_pct ?? p.discount_pct}
                        onChange={(e) => setEditing((prev) => ({...prev, [p.sku]: {...(prev[p.sku] || {}), discount_pct: e.target.value }}))}
                        className="w-16 rounded border border-zinc-800 bg-zinc-900 px-1.5 py-0.5 text-right text-[11px] text-amber-300 focus:border-orange-500/60 focus:outline-none"
                      />
                      <span className="ml-0.5 text-[10px] text-zinc-500">%</span>
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-emerald-300 tabular-nums">{fmt(p.campaign_price, c)}</td>
                    <td className="px-3 py-2 text-right">
                      <input
                        data-testid={`share-${p.sku}`}
                        type="number" step="0.01" min="0"
                        value={draft.emag_share_ron ?? p.emag_share_ron}
                        onChange={(e) => setEditing((prev) => ({...prev, [p.sku]: {...(prev[p.sku] || {}), emag_share_ron: e.target.value }}))}
                        className="w-20 rounded border border-zinc-800 bg-zinc-900 px-1.5 py-0.5 text-right text-[11px] text-sky-300 focus:border-orange-500/60 focus:outline-none"
                      />
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-rose-300 tabular-nums">{fmt(p.final_discount_per_unit, c)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {p.profit_per_unit != null ? (
                        <div>
                          <div className={p.profit_per_unit >= 0 ? "text-emerald-300 font-semibold" : "text-rose-400 font-semibold"}>{fmt(p.profit_per_unit, c)}</div>
                          <div className="text-[10px] text-zinc-500">{p.profit_per_unit_pct != null ? `${p.profit_per_unit_pct}%` : ""}</div>
                        </div>
                      ) : <span className="text-zinc-600" title="Setează pret_achizitie + comisioane în Products page">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-zinc-100 tabular-nums">{p.qty_sold || 0}</td>
                    <td className="px-3 py-2 text-right text-zinc-300 tabular-nums">{fmt(p.sum_normal_total, c)}</td>
                    <td className="px-3 py-2 text-right text-rose-300 tabular-nums">{fmt(p.sum_discount_total, c)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-emerald-300 tabular-nums">{fmt(p.sum_real_total, c)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {p.profit_total != null ? (
                        <div>
                          <div className={p.profit_total >= 0 ? "text-emerald-300 font-semibold" : "text-rose-400 font-semibold"}>{fmt(p.profit_total, c)}</div>
                          <div className="text-[10px] text-zinc-500">{p.profit_per_unit_pct != null ? `${p.profit_per_unit_pct}% marjă` : ""}</div>
                        </div>
                      ) : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {hasDraft && (
                        <button
                          data-testid={`save-${p.sku}`}
                          onClick={() => saveRow(p.sku)}
                          className="rounded border border-orange-500/50 bg-orange-500/20 px-2 py-0.5 text-[10px] font-medium text-orange-100 hover:bg-orange-500/30"
                        >Salvează</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {applyModalOpen && (
        <div data-testid="apply-global-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="w-full max-w-sm rounded-xl border border-zinc-800 bg-zinc-950 p-5 shadow-2xl">
            <h3 className="text-sm font-semibold text-zinc-100">Setează {isVoucher ? "voucher" : "discount"} global</h3>
            <p className="mt-1 text-[11px] text-zinc-500">
              Se aplică pe toate cele {data.product_count} produse. Poți edita manual pe fiecare rând ulterior.
            </p>
            <div className="mt-4 flex items-center gap-2">
              <input
                data-testid="apply-global-input"
                type="number" step="0.1" min="0" max="100"
                value={applyValue}
                onChange={(e) => setApplyValue(e.target.value)}
                className="flex-1 rounded border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-orange-500/60 focus:outline-none"
              />
              <span className="text-sm text-zinc-400">%</span>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setApplyModalOpen(false)} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800">Renunță</button>
              <button
                data-testid="apply-global-submit"
                onClick={applyGlobal}
                disabled={savingConfig}
                className="rounded-md border border-orange-500/50 bg-orange-500/20 px-3 py-1.5 text-xs font-medium text-orange-100 hover:bg-orange-500/30 disabled:opacity-40"
              >{savingConfig ? "Se salvează..." : "Aplică"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, extra, compare, delta, tone, testId }) {
  const toneClass = tone === "emerald" ? "border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 to-zinc-950" :
                    tone === "amber" ? "border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-zinc-950" :
                    "border-zinc-800 bg-zinc-950";
  return (
    <div data-testid={testId} className={`rounded-xl border ${toneClass} p-4`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span className="text-xl font-bold text-zinc-100 tabular-nums">{value}</span>
        {extra && <span className="text-[11px] font-semibold text-emerald-300">{extra}</span>}
      </div>
      {compare && <div className="mt-1 text-[10px] text-zinc-500">{compare}</div>}
      {delta !== null && delta !== undefined && (
        <div className="mt-1"><LiftBadge value={delta} testId={testId ? `${testId}-lift` : undefined} /></div>
      )}
    </div>
  );
}

function TotalCard({ icon: Icon, label, value, suffix, tone, testId }) {
  const toneMap = { emerald: "text-emerald-400 bg-emerald-500/10", amber: "text-amber-400 bg-amber-500/10" };
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
        {Icon && <div className={`rounded-md p-1 ${toneMap[tone]}`}><Icon size={12} /></div>}
      </div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="text-xl font-bold text-zinc-100">{value}</span>
        {suffix && <span className="text-[10px] text-zinc-500">{suffix}</span>}
      </div>
    </div>
  );
}

export default function EmagCampaigns() {
  const [tab, setTab] = useState("running"); // running | upcoming | enrolled | available | detail
  const [items, setItems] = useState([]);
  const [enrolledItems, setEnrolledItems] = useState([]); // from local products aggregation
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [platform, setPlatform] = useState("emag_ro_fbe");

  const loadAvailable = useCallback(async (filter) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/available`, {
        params: { integration_key: platform, filter },
      });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Eroare: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, [platform]);

  const loadEnrolled = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}`);
      setEnrolledItems(r.data?.items || []);
    } catch (e) {
      toast.error("Eroare: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === "enrolled") loadEnrolled();
    else loadAvailable(tab);
  }, [tab, loadAvailable, loadEnrolled]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/refresh`, null, { params: { integration_key: platform } });
      toast.success("Cache campanii actualizat din eMAG");
      if (tab === "enrolled") await loadEnrolled(); else await loadAvailable(tab);
    } catch (e) {
      toast.error("Eroare: " + (e.response?.data?.detail || e.message));
    } finally { setRefreshing(false); }
  };

  const TABS = [
    { id: "running", label: "În desfășurare" },
    { id: "upcoming", label: "Viitoare" },
    { id: "enrolled", label: "Înscrise cu produse" },
    { id: "past", label: "Trecute" },
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4 sm:p-6" data-testid="emag-campaigns-page">
      {!selected && (
        <>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Megaphone className="text-orange-400" size={24} />
              <div>
                <h1 className="text-2xl font-bold text-zinc-100">Campanii eMAG</h1>
                <p className="text-xs text-zinc-500">Toate campaniile eMAG (înscrise + disponibile) sincronizate direct din API</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select value={platform} onChange={(e) => setPlatform(e.target.value)} data-testid="platform-selector"
                className="rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-xs text-zinc-100 focus:border-orange-500/40 focus:outline-none">
                {Object.entries(PLATFORM_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              <button onClick={refresh} disabled={refreshing} data-testid="refresh-btn"
                className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:border-orange-500/40 hover:text-orange-200 disabled:opacity-50">
                {refreshing ? "Se sincronizează..." : "↻ Sincronizează"}
              </button>
              {tab === "past" && (
                <>
                  <button
                    onClick={async () => {
                      try {
                        const r = await axios.post(`${API}/participations/backfill`);
                        toast.success(`Backfill: ${r.data.inserted} campanii adăugate din istoric (total: ${r.data.total_participations})`);
                        await loadAvailable(tab);
                      } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
                    }}
                    data-testid="backfill-participations-btn"
                    className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-200 hover:bg-emerald-500/20">
                    ⇩ Backfill istoric
                  </button>
                  <button
                    onClick={async () => {
                      const cidRaw = window.prompt(`ID campanie eMAG pe care vrei să o adaugi în „Trecute" (număr, ex: 2307)\nCont: ${platform}`);
                      if (!cidRaw) return;
                      const cid = parseInt(cidRaw.trim(), 10);
                      if (!cid) { toast.error("ID invalid"); return; }
                      try {
                        const r = await axios.post(`${API}/participations/add`, { integration_key: platform, campaign_id: cid });
                        toast.success(r.data.added_now ? `Adăugat campanie #${cid} în Trecute` : `Campania #${cid} era deja în Trecute`);
                        await loadAvailable(tab);
                      } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
                    }}
                    data-testid="add-participation-btn"
                    className="rounded-md border border-orange-500/30 bg-orange-500/10 px-3 py-1.5 text-xs font-medium text-orange-200 hover:bg-orange-500/20">
                    + Adaugă manual
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-1 border-b border-zinc-800">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  tab === t.id
                    ? "border-b-2 border-orange-500 text-orange-300"
                    : "border-b-2 border-transparent text-zinc-400 hover:text-zinc-200"
                }`}>
                {t.label}
              </button>
            ))}
          </div>

          {loading && <div className="p-8 text-center text-zinc-500">Se încarcă campaniile...</div>}
          {!loading && tab === "enrolled" && enrolledItems.length === 0 && (
            <div className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950 p-10 text-center">
              <Tag className="mx-auto text-zinc-600" size={32} />
              <p className="mt-2 text-sm text-zinc-500">Nicio campanie eMAG cu produse înscrise.</p>
            </div>
          )}
          {!loading && tab !== "enrolled" && items.length === 0 && (
            <div className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950 p-10 text-center">
              <Tag className="mx-auto text-zinc-600" size={32} />
              <p className="mt-2 text-sm text-zinc-500">Nicio campanie {tab === "available" ? "disponibilă" : tab === "upcoming" ? "viitoare" : "în desfășurare"} pentru acest cont.</p>
            </div>
          )}

          {!loading && tab === "enrolled" && enrolledItems.length > 0 && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {enrolledItems.map((c) => (
                <CampaignCard key={`${c.campaign_id}:${c.integration_key}`} camp={c} onOpen={setSelected} />
              ))}
            </div>
          )}
          {!loading && tab !== "enrolled" && items.length > 0 && (
            <div className="space-y-2">
              {items.map((c) => (
                <AvailableCampaignRow key={`${c.id}:${c.integration_key}`} camp={c} onOpen={setSelected} platform={platform} />
              ))}
            </div>
          )}
        </>
      )}
      {selected && (
        <CampaignDetail campaignId={selected.campaign_id} integrationKey={selected.integration_key} onBack={() => setSelected(null)} />
      )}
    </div>
  );
}

// Row for "Disponibile / Running / Upcoming" tabs (eMAG-native campaigns).
function AvailableCampaignRow({ camp, onOpen, platform }) {
  const [expanded, setExpanded] = useState(false);
  const [offers, setOffers] = useState(null);
  const [loadingOffers, setLoadingOffers] = useState(false);

  const statusStyle = {
    running: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    upcoming: "bg-sky-500/15 text-sky-300 border-sky-500/40",
    ended: "bg-zinc-800 text-zinc-500 border-zinc-700",
  }[camp.derived_status] || "bg-zinc-800 text-zinc-400 border-zinc-700";
  const statusLabel = {
    running: "În desfășurare", upcoming: "Viitoare", ended: "Încheiată",
  }[camp.derived_status] || camp.derived_status;

  const canEnroll = !camp.is_enrolled && camp.derived_status !== "ended";

  const loadOffers = async () => {
    if (offers || loadingOffers) { setExpanded(!expanded); return; }
    setLoadingOffers(true);
    setExpanded(true);
    try {
      const r = await axios.get(`${API}/${camp.id}/eligible-offers`, {
        params: { integration_key: platform },
      });
      setOffers(r.data?.results || r.data?.offers || r.data || []);
    } catch (e) {
      toast.error("Nu pot lua oferte eligibile: " + (e.response?.data?.detail || e.message));
    } finally { setLoadingOffers(false); }
  };

  const rule = camp.participationRules || {};

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 overflow-hidden" data-testid={`available-row-${camp.id}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <span className={`rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${statusStyle}`}>{statusLabel}</span>
        {camp.is_enrolled && (
          <span className="rounded-md border border-amber-500/40 bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold text-amber-200" data-testid="badge-enrolled">
            ⭑ ÎNSCRIS
          </span>
        )}
        <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-500">ID {camp.id}</span>
        <div className="flex-1 min-w-0">
          <div className="truncate text-sm font-semibold text-zinc-100">{camp.name}</div>
          <div className="mt-0.5 flex items-center gap-1 text-[11px] text-zinc-500">
            <Calendar size={11} />
            {camp.startDate?.slice(0, 10)} → {camp.endDate?.slice(0, 10)}
            {camp.endPreparationDate && (
              <span className="ml-2 text-amber-400/80">· Deadline propuneri: {camp.endPreparationDate.slice(0, 10)}</span>
            )}
          </div>
        </div>
        <button onClick={loadOffers} data-testid={`btn-eligible-${camp.id}`}
          className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:border-orange-500/40 hover:text-orange-200">
          {expanded ? "Ascunde" : (camp.is_enrolled ? "Vezi oferte" : "Vezi oferte eligibile")}
        </button>
        {!camp.is_enrolled && camp.derived_status !== "ended" && (
          <EnrollIntentButton camp={camp} />
        )}
        {camp.is_enrolled && (
          <button onClick={() => onOpen({ campaign_id: camp.id, integration_key: camp.integration_key })} data-testid={`btn-analysis-${camp.id}`}
            className="rounded-md bg-orange-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-500">
            Analiză vânzări →
          </button>
        )}
      </div>
      {rule.minAllowedDiscount && (
        <div className="border-t border-zinc-800 bg-zinc-900/30 px-4 py-2 text-[11px] text-zinc-400">
          <span className="font-medium text-zinc-300">Reguli:</span> {rule.minAllowedDiscount}
        </div>
      )}
      {expanded && (
        <div className="border-t border-zinc-800 bg-zinc-900/20 px-4 py-3">
          {loadingOffers && <div className="text-xs text-zinc-500">Se încarcă oferte eligibile...</div>}
          {!loadingOffers && offers && Array.isArray(offers) && offers.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase text-zinc-500">
                  <tr>
                    <th className="px-2 py-1 text-left">SKU</th>
                    <th className="px-2 py-1 text-left">Nume</th>
                    <th className="px-2 py-1 text-right">Preț curent</th>
                    <th className="px-2 py-1 text-right">Preț min. campanie</th>
                    <th className="px-2 py-1 text-right">Stoc</th>
                  </tr>
                </thead>
                <tbody>
                  {offers.slice(0, 30).map((o, i) => (
                    <tr key={i} className="border-t border-zinc-800/60">
                      <td className="px-2 py-1 font-mono text-orange-300">{o.part_number || o.sku || o.id || "—"}</td>
                      <td className="px-2 py-1 truncate max-w-xs text-zinc-300">{o.name || "—"}</td>
                      <td className="px-2 py-1 text-right text-zinc-200">{o.sale_price ?? o.current_price ?? "—"}</td>
                      <td className="px-2 py-1 text-right text-emerald-300">{o.min_campaign_sale_price ?? o.min_sale_price ?? "—"}</td>
                      <td className="px-2 py-1 text-right text-zinc-400">{o.stock ?? o.general_stock ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {offers.length > 30 && (
                <div className="mt-2 text-[10px] text-zinc-500">+ încă {offers.length - 30} oferte (limita afișare 30)</div>
              )}
            </div>
          )}
          {!loadingOffers && offers && (!Array.isArray(offers) || offers.length === 0) && (
            <div className="text-xs text-zinc-500">Nicio ofertă eligibilă pentru acest cont pe această campanie.</div>
          )}
        </div>
      )}
    </div>
  );
}

function EnrollIntentButton({ camp }) {
  const [wants, setWants] = useState(!!camp.wants_to_enroll);
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    const next = !wants;
    try {
      await axios.post(
        `${API}/${camp.id}/intent?integration_key=${encodeURIComponent(camp.integration_key)}`,
        { wants_to_enroll: next },
      );
      setWants(next);
      toast.success(next
        ? "Am notat că vrei să te înscrii. Îți amintesc la 7/3/1 zile înainte."
        : "Am oprit reminderele pentru această campanie.");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  return (
    <button
      data-testid={`intent-btn-${camp.id}`}
      onClick={toggle}
      disabled={busy}
      title={wants ? "Reminder activ (click ca să oprești)" : "Marchează că vrei să te înscriu"}
      className={`rounded-md border px-3 py-1.5 text-xs font-medium disabled:opacity-40 ${
        wants
          ? "border-amber-500/60 bg-amber-500/25 text-amber-100 hover:bg-amber-500/35"
          : "border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-amber-500/40 hover:text-amber-200"
      }`}
    >
      {wants ? "⏰ Reminder activ" : "★ Vreau să mă înscriu"}
    </button>
  );
}

